import React, { useState } from 'react';
import { getAuth, signInWithCustomToken } from "@firebase/auth";
import { GoogleOAuthProvider, GoogleLogin, CredentialResponse } from '@react-oauth/google';
import { GoogleCircleFilled, ExclamationCircleOutlined } from "@ant-design/icons";
import { Button, Alert, Typography, Space } from 'antd';
import { useMutation } from '@apollo/client';
import { gauth_query, getGAuthVar } from "../../graphql/gauth";
import { ApolloResult } from '../../store';

const { Text } = Typography;
const clientId = process.env.REACT_APP_GOOGLE_CREDENTIALS_CLIENT_ID || '';

type S = { error: boolean, loading: boolean, errorMessage: string }

export default function LoginForm() {
    const [auth, { loading, error, data }] = useMutation<ApolloResult<"gauth", string>>(gauth_query)
    const [state, setState] = useState({ 
        error: false, 
        loading: false, 
        errorMessage: '' 
    } as S);

    const handleGLoginSuccess = async (response: CredentialResponse) => {
        if (!response.credential) return;
        setState({
            loading: true,
            error: false,
            errorMessage: ''
        })

        try {
            const r = await auth({ variables: getGAuthVar(response.credential) });
            if (r.data?.gauth) {
                await signInWithCustomToken(getAuth(), r.data.gauth);
            } else {
                setState({
                    loading: false,
                    error: true,
                    errorMessage: 'Authentication failed. Please try again.'
                })
            }
        } catch (error) {
            setState({
                loading: false,
                error: true,
                errorMessage: 'An error occurred during sign in. Please try again.'
            })
        }
    }

    const handleGLoginError = () => {
        setState({
            loading: false,
            error: true,
            errorMessage: 'Google sign in was cancelled or failed.'
        })
    }

    return (
        <div style={styles.container}>
            <Space direction="vertical" size="large" style={styles.content}>
                {/* Error Alert */}
                {state.error && (
                    <Alert
                        message="Sign In Error"
                        description={state.errorMessage}
                        type="error"
                        showIcon
                        icon={<ExclamationCircleOutlined />}
                        style={styles.errorAlert}
                        closable
                        onClose={() => setState(prev => ({ ...prev, error: false, errorMessage: '' }))}
                    />
                )}

                {/* Google Sign In Button */}
                <div style={styles.buttonContainer}>
                    <GoogleOAuthProvider clientId={clientId}>
                        {state.loading ? (
                            <Button
                                loading={true}
                                type="primary"
                                icon={<GoogleCircleFilled />}
                                style={styles.loadingButton}
                                size="large"
                                block
                            >
                                Signing in...
                            </Button>
                        ) : (
                            <div style={styles.googleButtonWrapper}>
                                <GoogleLogin
                                    text="signin_with"
                                    size="large"
                                    onSuccess={handleGLoginSuccess}
                                    onError={handleGLoginError}
                                />
                            </div>
                        )}
                    </GoogleOAuthProvider>
                </div>

                {/* Additional Info */}
                <div style={styles.infoSection}>
                    <Text type="secondary" style={styles.infoText}>
                        <strong>Secure Sign In:</strong> Your credentials are protected with Google's enterprise-grade security.
                    </Text>
                </div>
            </Space>
        </div>
    );
}

const styles = {
    container: {
        width: '100%',
    },
    content: {
        width: '100%',
    },
    errorAlert: {
        borderRadius: '8px',
        border: 'none',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    },
    buttonContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '60px',
    },
    loadingButton: {
        height: '48px',
        borderRadius: '8px',
        backgroundColor: '#1890ff',
        border: 'none',
        boxShadow: '0 4px 12px rgba(24, 144, 255, 0.3)',
        fontWeight: 500,
    },
    googleButtonWrapper: {
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '8px',
        borderRadius: '8px',
        backgroundColor: '#ffffff',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        border: '1px solid #e8e8e8',
        transition: 'all 0.3s ease',
        '&:hover': {
            boxShadow: '0 6px 16px rgba(0, 0, 0, 0.15)',
            transform: 'translateY(-1px)',
        },
    },
    infoSection: {
        textAlign: 'center' as const,
        padding: '16px 0',
    },
    infoText: {
        fontSize: '14px',
        lineHeight: '1.5',
        color: '#8c8c8c',
    },
};
